/**
 * ตัวเรียก API
 *
 * รับผิดชอบสามเรื่อง: แนบ header ที่จำเป็น, แปลง error ให้เป็นชนิดเดียวกันทั้งแอป
 * และ retry เฉพาะ error ที่ retry ได้จริงด้วย exponential backoff + jitter
 */
import type { ApiErrorBody, ErrorCode } from '../../shared/errors';
import { ERROR_MESSAGES_TH, isRetryableCode } from '../../shared/errors';
import type { AppSettings } from '../../shared/config';
import type { CaptureView, Paginated, Role } from '../../shared/types';

export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly status: number;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(
    code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    status: number,
    details?: unknown,
    requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.requestId = requestId;
  }

  /** error ที่ควรลองใหม่เอง ไม่ต้องรบกวนผู้ใช้ */
  get retryable(): boolean {
    return this.code === 'NETWORK_ERROR' || isRetryableCode(this.code);
  }
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  /** ส่ง Idempotency-Key ให้ endpoint ที่เขียนข้อมูล */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** จำนวนครั้งที่ลองใหม่เมื่อเจอ error ที่ retry ได้ */
  retries?: number;
  headers?: Record<string, string>;
  rawBody?: BodyInit;
}

const DEFAULT_RETRIES = 2;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(new DOMException('aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

/**
 * exponential backoff พร้อม jitter
 * jitter สำคัญตอนเน็ตกลับมาพร้อมกันหลายเครื่อง ถ้าไม่มีจะยิงชนกันเป็นระลอก
 */
function backoffMs(attempt: number): number {
  const base = Math.min(1000 * 2 ** attempt, 8000);
  return base / 2 + Math.random() * (base / 2);
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const retries = options.retries ?? DEFAULT_RETRIES;
  let lastError: ApiError | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) await sleep(backoffMs(attempt - 1), options.signal);

    const headers: Record<string, string> = { ...options.headers };
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (options.idempotencyKey) headers['idempotency-key'] = options.idempotencyKey;
    // แนบ token กัน CSRF ทุกครั้งที่ไม่ใช่การอ่านข้อมูล server บังคับให้ต้องมี
    const method = (options.method ?? 'GET').toUpperCase();
    if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      headers['x-csrf-token'] = csrfToken;
    }

    let response: Response;
    try {
      response = await fetch(path, {
        method: options.method ?? (options.body !== undefined ? 'POST' : 'GET'),
        headers,
        body: options.rawBody ?? (options.body !== undefined ? JSON.stringify(options.body) : null),
        credentials: 'same-origin',
        signal: options.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      lastError = new ApiError(
        'NETWORK_ERROR',
        'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ระบบจะลองใหม่อัตโนมัติ',
        0,
      );
      continue;
    }

    if (response.ok) {
      if (response.status === 204) return undefined as T;
      const text = await response.text();
      return (text ? JSON.parse(text) : undefined) as T;
    }

    const requestId = response.headers.get('x-request-id') ?? undefined;
    let body: ApiErrorBody | null;
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = null;
    }

    const code = (body?.error?.code ?? 'INTERNAL_ERROR') as ErrorCode;
    const error = new ApiError(
      code,
      body?.error?.message ?? ERROR_MESSAGES_TH.INTERNAL_ERROR,
      response.status,
      body?.error?.details,
      requestId,
    );

    if (!error.retryable) throw error;
    lastError = error;
  }

  throw lastError ?? new ApiError('INTERNAL_ERROR', ERROR_MESSAGES_TH.INTERNAL_ERROR, 500);
}

// ---------------------------------------------------------------- csrf

/**
 * token กัน CSRF ของ session ปัจจุบัน
 *
 * เก็บไว้ในหน่วยความจำเท่านั้น ไม่เก็บลง localStorage
 * เพราะถ้าเก็บในที่ที่ JavaScript อื่นอ่านได้ ก็ไม่ได้ป้องกันอะไรเพิ่ม
 * ค่านี้ได้มาจากผลของการล็อกอิน หรือจาก /api/me ตอนเปิดแอป
 */
let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

// ---------------------------------------------------------------- endpoints

export interface SessionResponse {
  user: {
    id: string;
    email: string;
    displayName: string;
    role: Role;
    areaCode: string | null;
  };
  settings: Pick<AppSettings, 'registration' | 'gps' | 'image' | 'capture'> & {
    auth: { minPasswordLength: number };
  };
  csrfToken: string | null;
  mustChangePassword: boolean;
  serverTime: string;
}

export interface LoginResponse {
  user: SessionResponse['user'];
  csrfToken: string;
  mustChangePassword: boolean;
  expiresAt: string;
}

export interface UploadAuthorization {
  url: string;
  token: string;
  expiresAt: string;
  maxBytes: number;
  contentType: 'image/jpeg' | 'image/webp' | 'image/png';
}

export interface DraftResponse {
  capture: CaptureView;
  upload: UploadAuthorization;
  awaitingGpsApproval: boolean;
  duplicateOf: string[];
}

export interface FinalizeResponse {
  capture: CaptureView;
  awaitingGpsApproval: boolean;
}

export interface DashboardSummary {
  total: number;
  byStatus: Record<string, number>;
  gpsOverrideRequested: number;
  gpsOverrideApproved: number;
  duplicateRegistrations: number;
  todayCompleted: number;
  last7DaysCompleted: number;
}

export interface ExportJobView {
  id: string;
  status: 'queued' | 'running' | 'ready' | 'failed' | 'expired';
  rowsWritten: number;
  sizeBytes: number | null;
  errorCode: string | null;
  expiresAt: string | null;
  /** true = ข้อมูลที่ตรงตัวกรองมีมากกว่าเพดาน ไฟล์จึงได้แค่บางส่วน */
  truncated: boolean;
  maxRows: number;
}

export const api = {
  session: async (signal?: AbortSignal) => {
    const response = await apiFetch<SessionResponse>('/api/me', { signal });
    // เก็บ token ไว้ให้ request ที่เขียนข้อมูลใช้ต่อ
    setCsrfToken(response.csrfToken);
    return response;
  },

  login: async (email: string, password: string) => {
    const response = await apiFetch<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
      retries: 0,
    });
    setCsrfToken(response.csrfToken);
    return response;
  },

  logout: async () => {
    try {
      await apiFetch<{ loggedOut: boolean }>('/api/auth/logout', { method: 'POST', retries: 0 });
    } finally {
      // ล้าง token ฝั่ง client เสมอ แม้ server ตอบไม่สำเร็จ
      setCsrfToken(null);
    }
  },

  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<{ changed: boolean }>('/api/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
      retries: 0,
    }),

  resetUserPassword: (userId: string) =>
    apiFetch<{ temporaryPassword: string; user: { email: string; displayName: string } }>(
      `/api/admin/users/${userId}/reset-password`,
      { method: 'POST', body: {}, retries: 0 },
    ),

  revokeUserSessions: (userId: string) =>
    apiFetch<{ revokedSessions: number }>(`/api/admin/users/${userId}/revoke-sessions`, {
      method: 'POST',
      body: {},
      retries: 0,
    }),

  createDraft: (body: unknown, idempotencyKey: string, signal?: AbortSignal) =>
    apiFetch<DraftResponse>('/api/captures/draft', {
      method: 'POST',
      body,
      idempotencyKey,
      signal,
      // ไม่ retry อัตโนมัติที่ชั้นนี้ ปล่อยให้คิวออฟไลน์เป็นผู้ควบคุมจังหวะการลองใหม่
      retries: 0,
    }),

  reissueUpload: (captureId: string, body: unknown) =>
    apiFetch<{ upload: UploadAuthorization }>(`/api/captures/${captureId}/upload-url`, {
      method: 'POST',
      body,
      retries: 1,
    }),

  uploadImage: (captureId: string, blob: Blob, token: string, sha256: string) =>
    apiFetch<{ objectStored: boolean; sizeBytes: number }>(`/api/captures/${captureId}/image`, {
      method: 'PUT',
      rawBody: blob,
      retries: 0,
      headers: {
        'x-upload-token': token,
        'x-image-sha256': sha256,
        'content-type': blob.type,
      },
    }),

  finalize: (captureId: string, body: unknown, idempotencyKey: string) =>
    apiFetch<FinalizeResponse>(`/api/captures/${captureId}/finalize`, {
      method: 'POST',
      body,
      idempotencyKey,
      retries: 0,
    }),

  listCaptures: (query: string, signal?: AbortSignal) =>
    apiFetch<Paginated<CaptureView> & { items: CaptureView[] }>(`/api/captures?${query}`, {
      signal,
    }),

  getCapture: (id: string, signal?: AbortSignal) =>
    apiFetch<{ capture: CaptureView }>(`/api/captures/${id}`, { signal }),

  imageUrl: (id: string, signal?: AbortSignal) =>
    apiFetch<{ url: string; expiresAt: string }>(`/api/captures/${id}/image-url`, { signal }),

  requestOverride: (id: string, reason: string) =>
    apiFetch<{ capture: CaptureView }>(`/api/captures/${id}/gps-override-request`, {
      method: 'POST',
      body: { reason },
    }),

  decideOverride: (id: string, decision: 'approved' | 'rejected', note?: string) =>
    apiFetch<{ capture: CaptureView }>(`/api/captures/${id}/gps-override-approve`, {
      method: 'POST',
      body: { decision, ...(note ? { note } : {}) },
    }),

  /**
   * ลบรายการ
   *
   * ต้องส่ง version ที่โหลดมาด้วย ถ้ามีคนแก้รายการนี้ระหว่างที่หน้าจอเปิดค้างอยู่
   * จะได้ VERSION_CONFLICT แทนที่จะลบทับข้อมูลที่เพิ่งเปลี่ยน
   *
   * confirm: true เป็นด่านฝั่ง API ที่ผู้ว่าจ้างกำหนด ไม่ใช่ตัวยืนยันที่ผู้ใช้เห็น
   * ตัวที่ผู้ใช้เห็นคือ ConfirmDialog ซึ่งต้องกดยืนยันก่อนถึงจะมาเรียกฟังก์ชันนี้
   * retries: 0 เพราะการลบไม่ควรถูกส่งซ้ำเองโดยอัตโนมัติ
   */
  voidCapture: (id: string, reason: string, version: number) =>
    apiFetch<{ capture: CaptureView }>(`/api/captures/${id}/void`, {
      method: 'POST',
      body: { reason, version, confirm: true },
      retries: 0,
    }),

  auditFor: (id: string, signal?: AbortSignal) =>
    apiFetch<
      Paginated<{ id: string; action: string; createdAt: string; actorUserId: string | null }>
    >(`/api/captures/${id}/audit`, { signal }),

  dashboard: (signal?: AbortSignal) =>
    apiFetch<DashboardSummary>('/api/dashboard/summary', { signal }),

  createExport: (body: unknown) =>
    apiFetch<{ job: ExportJobView }>('/api/exports', { method: 'POST', body }),

  getExport: (id: string, signal?: AbortSignal) =>
    apiFetch<{ job: ExportJobView }>(`/api/exports/${id}`, { signal }),

  /**
   * ลิงก์ดาวน์โหลดไฟล์ export
   *
   * เป็น URL ธรรมดาที่ให้เบราว์เซอร์โหลดเอง ไม่ใช่ fetch แล้วสร้าง blob
   * เพราะไฟล์อาจใหญ่ถึงหลายสิบเมกะไบต์ การดึงเข้าหน่วยความจำก่อนไม่จำเป็น
   * cookie เป็น same-origin เบราว์เซอร์แนบให้เองอยู่แล้ว
   */
  exportDownloadUrl: (id: string) => `/api/exports/${id}/download`,
};
